import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Card, Grid, MenuItem, TextField, Button,
  Divider, Tabs, Tab, CircularProgress, Chip, IconButton,
  ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableHead, TableRow, Paper
} from '@mui/material';
import { ChevronLeft, ChevronRight, GppMaybe, CheckCircle, Schedule, FileDownload } from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/es';
import { reportsAPI, projectsAPI, usersAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DevBadge from '../../components/common/DevBadge';

dayjs.extend(isoWeek);
dayjs.locale('es');

// ── Helper: trigger blob download ─────────────────────────────────────────
function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const CHART_COLORS =   ['#4FC3F7', '#81C784', '#FFB74D', '#F06292', '#CE93D8', '#80DEEA', '#FFCC02', '#FF8A65'];
// Intense warm colors for overtime segments — vivid and well-spaced to stay distinct
const OVERTIME_COLORS = ['#B71C1C', '#E65100', '#880E4F', '#F57F17', '#AD1457', '#DD2C00', '#6D001A', '#C51162'];

function TabPanel({ children, value, index }) {
  return value === index ? <Box mt={2}>{children}</Box> : null;
}

function DisputeCards({ disputeStats, labelTotal = 'Refutaciones generadas' }) {
  if (!disputeStats || disputeStats.total === 0) return null;
  return (
    <Grid container spacing={2} mb={3}>
      {[
        { label: labelTotal, value: disputeStats.total, icon: <GppMaybe />, color: '#FFB74D' },
        { label: 'Pendientes', value: disputeStats.pending, icon: <Schedule />, color: '#F06292' },
        { label: 'Resueltas', value: disputeStats.resolved, icon: <CheckCircle />, color: '#81C784' }
      ].map(s => (
        <Grid item xs={12} sm={4} key={s.label}>
          <Card sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ color: s.color }}>{s.icon}</Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
              <Typography variant="caption" color="text.secondary">{s.label}</Typography>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

export default function Reports() {
  const [tab, setTab] = useState(0);

  // ── Period selector state ─────────────────────────────────────────────────
  const [periodMode, setPeriodMode] = useState('week'); // 'week' | 'month'
  const [weekStart, setWeekStart] = useState(dayjs().startOf('isoWeek'));
  const [monthDate, setMonthDate] = useState(dayjs().startOf('month'));

  // ── Selectors ─────────────────────────────────────────────────────────────
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedMember, setSelectedMember] = useState('');

  // ── Data ──────────────────────────────────────────────────────────────────
  const [projectData, setProjectData] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [availData, setAvailData] = useState(null);
  const [loadingProject, setLoadingProject] = useState(false);
  const [loadingMember, setLoadingMember] = useState(false);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [downloadingProject, setDownloadingProject] = useState(false);
  const [downloadingMember, setDownloadingMember] = useState(false);
  const [init, setInit] = useState(false);

  // ── Derived period dates ──────────────────────────────────────────────────
  const { startDate, endDate, periodLabel } = useMemo(() => {
    if (periodMode === 'week') {
      const s = weekStart;
      const e = weekStart.endOf('isoWeek');
      return {
        startDate: s.format('YYYY-MM-DD'),
        endDate: e.format('YYYY-MM-DD'),
        periodLabel: `S${s.isoWeek()} · ${s.format('D MMM')} – ${e.format('D MMM YYYY')}`
      };
    }
    const s = monthDate.startOf('month');
    const e = monthDate.endOf('month');
    const raw = monthDate.format('MMMM YYYY');
    return {
      startDate: s.format('YYYY-MM-DD'),
      endDate: e.format('YYYY-MM-DD'),
      periodLabel: raw.charAt(0).toUpperCase() + raw.slice(1)
    };
  }, [periodMode, weekStart, monthDate]);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([projectsAPI.getAll(), usersAPI.getAll()])
      .then(([prj, usr]) => {
        setProjects(prj.data);
        setMembers(usr.data.filter(u => u.role === 'member'));
      })
      .catch(console.error)
      .finally(() => setInit(true));
  }, []);

  // ── Fetch functions ───────────────────────────────────────────────────────
  const fetchProject = async () => {
    if (!selectedProject) return;
    setLoadingProject(true);
    try {
      const res = await reportsAPI.getProjectReport(selectedProject, { startDate, endDate });
      setProjectData(res.data);
    } catch { } finally { setLoadingProject(false); }
  };

  const fetchMember = async () => {
    if (!selectedMember) return;
    setLoadingMember(true);
    try {
      const res = await reportsAPI.getMemberReport(selectedMember, { startDate, endDate });
      setMemberData(res.data);
    } catch { } finally { setLoadingMember(false); }
  };

  const handleDownloadProjectExcel = async () => {
    if (!selectedProject) return;
    setDownloadingProject(true);
    try {
      const res = await reportsAPI.downloadProjectExcel(selectedProject, { startDate, endDate });
      const projectName = projects.find(p => p._id === selectedProject)?.name || 'proyecto';
      triggerBlobDownload(res.data, `actividades_proyecto_${projectName.replace(/[^a-z0-9]/gi, '_')}.xlsx`);
    } catch (e) {
      console.error('Error descargando Excel de proyecto:', e);
    } finally {
      setDownloadingProject(false);
    }
  };

  const handleDownloadMemberExcel = async () => {
    if (!selectedMember) return;
    setDownloadingMember(true);
    try {
      const res = await reportsAPI.downloadMemberExcel(selectedMember, { startDate, endDate });
      const member = members.find(m => m._id === selectedMember);
      const memberName = member
        ? (member.name ? `${member.name}_${member.lastName || ''}`.trim() : member.email)
        : selectedMember;
      triggerBlobDownload(res.data, `actividades_miembro_${memberName.replace(/[^a-z0-9]/gi, '_')}.xlsx`);
    } catch (e) {
      console.error('Error descargando Excel de miembro:', e);
    } finally {
      setDownloadingMember(false);
    }
  };

  const fetchAvail = async () => {
    setLoadingAvail(true);
    try {
      const res = await reportsAPI.getAvailability({ startDate, endDate });
      setAvailData(res.data);
    } catch { } finally { setLoadingAvail(false); }
  };

  // Auto-refresh on period change if data already loaded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (projectData && selectedProject) fetchProject(); }, [startDate, endDate]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (memberData && selectedMember) fetchMember(); }, [startDate, endDate]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 2) fetchAvail(); }, [tab, startDate, endDate]);

  // ── Chart builders ────────────────────────────────────────────────────────
  const pieOpts = (data, title) => ({
    chart: { type: 'pie', height: 300, backgroundColor: 'transparent' },
    title: { text: title, style: { fontSize: '13px' } },
    tooltip: { pointFormat: '<b>{point.y:.1f}h</b> ({point.percentage:.0f}%)' },
    plotOptions: { pie: { innerSize: '50%', dataLabels: { enabled: true, format: '{point.name}<br/><b>{point.y:.1f}h</b> ({point.percentage:.0f}%)', style: { fontSize: '11px', fontWeight: 'normal', textOutline: 'none' } }, colors: CHART_COLORS } },
    credits: { enabled: false },
    series: [{ name: 'Horas', data: (data || []).map(d => ({ name: d.name || 'N/A', y: d.y || 0 })) }]
  });

  // Single stacked bar per category (e.g. per member): normal hours + overtime hours
  const stackedNormalOvertimeOpts = (categories, normalData, overtimeData, title) => {
    const hasOvertime = (overtimeData || []).some(v => (v || 0) > 0);
    return {
      chart: { type: 'column', height: 300, backgroundColor: 'transparent' },
      title: { text: title, style: { fontSize: '13px' } },
      xAxis: { categories, gridLineWidth: 0, labels: { rotation: categories.length > 5 ? -45 : 0, style: { fontSize: '11px' } } },
      yAxis: { title: { text: 'Horas' }, gridLineDashStyle: 'dot' },
      tooltip: { shared: true, valueSuffix: 'h' },
      credits: { enabled: false },
      plotOptions: { column: { stacking: 'normal', borderRadius: 2, dataLabels: { enabled: true, formatter() { return this.y > 0 ? `${this.y.toFixed(1)}h` : null; }, style: { fontSize: '10px', fontWeight: 'normal', textOutline: 'none', color: '#fff' } } } },
      legend: { enabled: hasOvertime },
      series: [
        { name: 'Horas normales', data: (normalData || []).map(v => parseFloat((v || 0).toFixed(2))), color: CHART_COLORS[0] },
        { name: 'Horas extra', data: (overtimeData || []).map(v => parseFloat((v || 0).toFixed(2))), color: OVERTIME_COLORS[0] }
      ]
    };
  };

  const lineOpts = (categories, data, title) => ({
    chart: { type: 'column', height: 260, backgroundColor: 'transparent' },
    title: { text: title, style: { fontSize: '13px' } },
    xAxis: { categories, gridLineWidth: 0, labels: { rotation: categories.length > 7 ? -45 : 0, style: { fontSize: '11px' } } },
    yAxis: { title: { text: 'Horas' }, gridLineDashStyle: 'dot' },
    tooltip: { valueSuffix: 'h' },
    credits: { enabled: false },
    plotOptions: { column: { borderRadius: 2, color: '#4FC3F7', dataLabels: { enabled: true, format: '{point.y:.1f}h', style: { fontSize: '10px', fontWeight: 'normal', textOutline: 'none' } } } },
    legend: { enabled: false },
    series: [{ name: 'Horas', data: (data || []).map(v => parseFloat((v || 0).toFixed(2))), color: '#4FC3F7' }]
  });

  // Daily grouped bars per project (week mode) — same logic as weekly but keyed by date string
  const dailyByProjectOpts = (dailyByProject, title) => {
    if (!dailyByProject || dailyByProject.length === 0) {
      return {
        chart: { height: 280, backgroundColor: 'transparent' },
        title: { text: title, style: { fontSize: '13px' } },
        credits: { enabled: false },
        series: []
      };
    }
    const dateKeys = [...new Set(dailyByProject.map(d => d.date))].sort();
    const DAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const dateLabels = dateKeys.map(dk => {
      const [y, m, d] = dk.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      return `${DAY_LABELS[dt.getUTCDay()]} ${d}/${m}`;
    });
    const projectNames = [...new Set(dailyByProject.map(d => d.projectName))];
    const series = [];
    projectNames.forEach((projName, i) => {
      const baseColor = CHART_COLORS[i % CHART_COLORS.length];
      series.push({
        name: projName,
        type: 'column',
        stack: projName,
        data: dateKeys.map(dk => {
          const entry = dailyByProject.find(d => d.date === dk && d.projectName === projName);
          return parseFloat((entry?.normalHours ?? entry?.totalHours ?? 0).toFixed(2));
        }),
        color: baseColor,
        legendIndex: i * 2
      });
      const otColor = OVERTIME_COLORS[i % OVERTIME_COLORS.length];
      const hasOvertime = dailyByProject.some(d => d.projectName === projName && (d.overtimeHours || 0) > 0);
      series.push({
        name: hasOvertime ? `${projName} · Extra` : `_ot_${projName}`,
        type: 'column',
        stack: projName,
        data: dateKeys.map(dk => {
          const entry = dailyByProject.find(d => d.date === dk && d.projectName === projName);
          return parseFloat((entry?.overtimeHours || 0).toFixed(2));
        }),
        color: otColor,
        showInLegend: hasOvertime,
        legendIndex: i * 2 + 1
      });
    });
    return {
      chart: { type: 'column', height: 280, backgroundColor: 'transparent' },
      title: { text: title, style: { fontSize: '13px' } },
      xAxis: { categories: dateLabels, gridLineWidth: 0, labels: { style: { fontSize: '11px' } } },
      yAxis: { title: { text: 'Horas' }, gridLineDashStyle: 'dot' },
      tooltip: {
        shared: false,
        formatter() {
          const projName = this.series.options.stack;
          const day = this.x;
          const allForProj = this.series.chart.series.filter(s => s.options.stack === projName);
          const total = allForProj.reduce((sum, s) => sum + (s.data[this.point.index]?.y || 0), 0);
          const isOt = this.series.name.includes('· Extra');
          const color = this.series.color;
          return `<b>${projName}</b> · ${day}<br/>${isOt ? `<span style="color:${color}">● Extra</span>` : '<span>● Normal</span>'}: <b>${this.y.toFixed(2)}h</b><br/>Total proyecto: <b>${total.toFixed(2)}h</b>`;
        }
      },
      credits: { enabled: false },
      plotOptions: { column: { stacking: 'normal', grouping: true, borderRadius: 2, dataLabels: { enabled: true, formatter() { return this.y > 0 ? `${this.y.toFixed(1)}h` : null; }, style: { fontSize: '9px', fontWeight: 'normal', textOutline: 'none', color: '#fff' } } } },
      legend: { enabled: true, maxHeight: 70, itemStyle: { fontSize: '11px' } },
      series
    };
  };

  // Weekly stacked bars by project
  // Grouped bars per project, each bar split into normal + overtime
  const weeklyByProjectStackedOpts = (weeklyByProject, title) => {
    if (!weeklyByProject || weeklyByProject.length === 0) {
      return {
        chart: { height: 280, backgroundColor: 'transparent' },
        title: { text: title, style: { fontSize: '13px' } },
        credits: { enabled: false },
        series: []
      };
    }
    const weekKeys = [...new Set(weeklyByProject.map(w => `${w.year}-${String(w.week).padStart(2, '0')}`))]  .sort();
    const weekLabels = weekKeys.map(k => `S${k.split('-')[1]}`);
    const projectNames = [...new Set(weeklyByProject.map(w => w.projectName))];

    // Two series per project: normal hours (base color) + overtime hours (orange tint).
    // Each project uses the same `stack` id so both segments are stacked inside
    // a single grouped column, while different projects are placed side by side.
    const series = [];
    projectNames.forEach((projName, i) => {
      const baseColor = CHART_COLORS[i % CHART_COLORS.length];
      // Normal hours segment
      series.push({
        name: projName,
        type: 'column',
        stack: projName,
        data: weekKeys.map(wk => {
          const [yr, wkN] = wk.split('-').map(Number);
          const entry = weeklyByProject.find(w => w.year === yr && w.week === wkN && w.projectName === projName);
          return parseFloat((entry?.normalHours ?? entry?.totalHours ?? 0).toFixed(2));
        }),
        color: baseColor,
        legendIndex: i * 2
      });
      // Overtime segment (only add legend entry if there is any overtime for this project)
      const otColor = OVERTIME_COLORS[i % OVERTIME_COLORS.length];
      const hasOvertime = weeklyByProject.some(w => w.projectName === projName && (w.overtimeHours || 0) > 0);
      series.push({
        name: hasOvertime ? `${projName} · Extra` : `_ot_${projName}`,
        type: 'column',
        stack: projName,
        data: weekKeys.map(wk => {
          const [yr, wkN] = wk.split('-').map(Number);
          const entry = weeklyByProject.find(w => w.year === yr && w.week === wkN && w.projectName === projName);
          return parseFloat((entry?.overtimeHours || 0).toFixed(2));
        }),
        color: otColor,
        showInLegend: hasOvertime,
        legendIndex: i * 2 + 1
      });
    });

    return {
      chart: { type: 'column', height: 280, backgroundColor: 'transparent' },
      title: { text: title, style: { fontSize: '13px' } },
      xAxis: { categories: weekLabels, gridLineWidth: 0, labels: { style: { fontSize: '11px' } } },
      yAxis: { title: { text: 'Horas' }, gridLineDashStyle: 'dot' },
      tooltip: {
        shared: false,
        formatter() {
          const projName = this.series.options.stack;
          const week = this.x;
          const allForProj = this.series.chart.series.filter(s => s.options.stack === projName);
          const total = allForProj.reduce((sum, s) => sum + (s.data[this.point.index]?.y || 0), 0);
          const isOt = this.series.name.includes('· Extra');
          const color = this.series.color;
          return `<b>${projName}</b> · ${week}<br/>${isOt ? `<span style="color:${color}">● Extra</span>` : '<span>● Normal</span>'}: <b>${this.y.toFixed(2)}h</b><br/>Total proyecto: <b>${total.toFixed(2)}h</b>`;
        }
      },
      credits: { enabled: false },
      plotOptions: { column: { stacking: 'normal', grouping: true, borderRadius: 2, dataLabels: { enabled: true, formatter() { return this.y > 0 ? `${this.y.toFixed(1)}h` : null; }, style: { fontSize: '9px', fontWeight: 'normal', textOutline: 'none', color: '#fff' } } } },
      legend: { enabled: true, maxHeight: 70, itemStyle: { fontSize: '11px' } },
      series
    };
  };

  // Stacked columns per member (normal + overtime segments) + spline total line
  const comboOpts = (flatData, title) => {
    if (!flatData || flatData.length === 0) {
      return {
        chart: { height: 320, backgroundColor: 'transparent' },
        title: { text: title, style: { fontSize: '13px' } },
        credits: { enabled: false },
        series: []
      };
    }
    const dates = [...new Set(flatData.map(d => d.date))].sort();
    const memberLabels = [...new Set(flatData.map(d => d.email))];
    const memberSeries = [];
    memberLabels.forEach((label, i) => {
      const baseColor = CHART_COLORS[i % CHART_COLORS.length];
      memberSeries.push({
        name: label,
        type: 'column',
        stack: label,
        data: dates.map(date => {
          const entry = flatData.find(d => d.date === date && d.email === label);
          return parseFloat((entry?.normalHours ?? entry?.hours ?? 0).toFixed(2));
        }),
        color: baseColor,
        legendIndex: i * 2
      });
      const otColor = OVERTIME_COLORS[i % OVERTIME_COLORS.length];
      const hasOvertime = flatData.some(d => d.email === label && (d.overtimeHours || 0) > 0);
      memberSeries.push({
        name: hasOvertime ? `${label} · Extra` : `_ot_${label}`,
        type: 'column',
        stack: label,
        data: dates.map(date => {
          const entry = flatData.find(d => d.date === date && d.email === label);
          return parseFloat((entry?.overtimeHours || 0).toFixed(2));
        }),
        color: otColor,
        showInLegend: hasOvertime,
        legendIndex: i * 2 + 1
      });
    });
    const totals = dates.map(date =>
      parseFloat(flatData.filter(d => d.date === date).reduce((s, d) => s + (d.hours || 0), 0).toFixed(2))
    );
    return {
      chart: { type: 'column', height: 340, backgroundColor: 'transparent' },
      title: { text: title, style: { fontSize: '13px' } },
      xAxis: { categories: dates, gridLineWidth: 0, labels: { rotation: dates.length > 8 ? -45 : 0, style: { fontSize: '11px' } } },
      yAxis: { title: { text: 'Horas' }, gridLineDashStyle: 'dot' },
      tooltip: { shared: true, valueSuffix: 'h' },
      credits: { enabled: false },
      plotOptions: { column: { stacking: 'normal', borderRadius: 2, dataLabels: { enabled: true, formatter() { return this.y > 0 ? `${this.y.toFixed(1)}h` : null; }, style: { fontSize: '9px', fontWeight: 'normal', textOutline: 'none', color: '#fff' } } } },
      legend: { enabled: true, maxHeight: 60 },
      series: [
        ...memberSeries,
        { name: 'Total', type: 'spline', data: totals, color: '#9E9E9E', lineWidth: 2.5,
          marker: { radius: 3 }, zIndex: 5, dashStyle: 'ShortDash',
          dataLabels: { enabled: true, format: '{point.y:.1f}h', style: { fontSize: '10px', fontWeight: 'bold', textOutline: 'none', color: '#555' } } }
      ]
    };
  };

  // ── Period navigator ──────────────────────────────────────────────────────
  const handlePrev = () => {
    if (periodMode === 'week') setWeekStart(w => w.subtract(1, 'week'));
    else setMonthDate(m => m.subtract(1, 'month'));
  };
  const handleNext = () => {
    if (periodMode === 'week') setWeekStart(w => w.add(1, 'week'));
    else setMonthDate(m => m.add(1, 'month'));
  };

  if (!init) return <LoadingSpinner />;

  return (
    <Box>
      <DevBadge id="page:teamlead-reportes" />

      {/* Header + period selector */}
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" mb={3} gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} mb={0.5}>Reportes</Typography>
          <Typography variant="body2" color="text.secondary">
            Métricas de rendimiento de proyectos y miembros
          </Typography>
        </Box>

        {/* Period selector */}
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup value={periodMode} exclusive size="small"
            onChange={(_, v) => v && setPeriodMode(v)}
            sx={{ '& .MuiToggleButton-root': { height: 32, py: 0 } }}>
            <ToggleButton value="week" sx={{ px: 2 }}>Semana</ToggleButton>
            <ToggleButton value="month" sx={{ px: 2 }}>Mes</ToggleButton>
          </ToggleButtonGroup>
          <Box display="flex" alignItems="center" gap={0.5} height={32}>
            <IconButton size="small" onClick={handlePrev} sx={{ height: 32, width: 32 }}><ChevronLeft fontSize="small" /></IconButton>
            <Chip label={periodLabel} variant="outlined" size="small"
              sx={{ fontSize: '0.78rem', minWidth: 180, textTransform: 'capitalize', height: 32, borderRadius: '4px' }} />
            <IconButton size="small" onClick={handleNext} sx={{ height: 32, width: 32 }}><ChevronRight fontSize="small" /></IconButton>
          </Box>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label="Por Proyecto" />
        <Tab label="Por Miembro" />
        <Tab label="Disponibilidad" />
      </Tabs>
      <Divider />

      {/* ── Tab 0: Por Proyecto */}
      <TabPanel value={tab} index={0}>
        <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
          <TextField select label="Seleccionar Proyecto" value={selectedProject} size="small"
            onChange={e => { setSelectedProject(e.target.value); setProjectData(null); }}
            sx={{ minWidth: 260 }}>
            {projects.map(p => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
          </TextField>
          <Button variant="contained" size="small" onClick={fetchProject} disabled={!selectedProject || loadingProject}
            sx={{ height: 40 }}>
            {loadingProject ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Consultar'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={downloadingProject ? <CircularProgress size={14} /> : <FileDownload fontSize="small" />}
            onClick={handleDownloadProjectExcel}
            disabled={!selectedProject || downloadingProject}
            sx={{ height: 40 }}
          >
            Descargar Excel
          </Button>
        </Box>

        {projectData && (
          <>
            <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
              <Chip label={`${(projectData.summary?.totalHours || 0).toFixed(1)}h totales`} color="primary" size="small" />
              {projectData.summary?.totalOvertimeHours > 0 && (
                <Chip label={`+${projectData.summary.totalOvertimeHours.toFixed(1)}h extra`} color="warning" size="small" />
              )}
              <Chip label={`${projectData.summary?.membersCount || 0} miembros`} variant="outlined" size="small" />
            </Box>
            <DisputeCards disputeStats={projectData.disputeStats} labelTotal="Refutaciones generadas" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2 }}>
                  <HighchartsReact highcharts={Highcharts}
                    options={pieOpts(projectData.charts?.hoursByCategory, 'Horas por Categoría')} />
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2 }}>
                  <HighchartsReact highcharts={Highcharts} options={stackedNormalOvertimeOpts(
                    projectData.charts?.hoursByMember?.categories || [],
                    projectData.charts?.hoursByMember?.normalData || [],
                    projectData.charts?.hoursByMember?.overtimeData || [],
                    'Horas por Miembro'
                  )} />
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card sx={{ p: 2 }}>
                  <HighchartsReact highcharts={Highcharts}
                    options={comboOpts(
                      projectData.charts?.dailyHoursByMember || [],
                      'Horas Diarias por Miembro (barras) y Total acumulado (línea)'
                    )} />
                </Card>
              </Grid>
              {(projectData.charts?.hoursByMilestone?.length > 0) && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2 }}>
                    <HighchartsReact highcharts={Highcharts}
                      options={pieOpts(projectData.charts.hoursByMilestone, 'Horas por Hito')} />
                  </Card>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {!projectData && !loadingProject && (
          <Box py={6} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Selecciona un proyecto y presiona Consultar.
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* ── Tab 1: Por Miembro */}
      <TabPanel value={tab} index={1}>
        <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
          <TextField select label="Seleccionar Miembro" value={selectedMember} size="small"
            onChange={e => { setSelectedMember(e.target.value); setMemberData(null); }}
            sx={{ minWidth: 260 }}>
            {members.map(m => (
              <MenuItem key={m._id} value={m._id}>
                {m.name ? `${m.name} ${m.lastName || ''}`.trim() : m.email}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" size="small" onClick={fetchMember} disabled={!selectedMember || loadingMember}
            sx={{ height: 40 }}>
            {loadingMember ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Consultar'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={downloadingMember ? <CircularProgress size={14} /> : <FileDownload fontSize="small" />}
            onClick={handleDownloadMemberExcel}
            disabled={!selectedMember || downloadingMember}
            sx={{ height: 40 }}
          >
            Descargar Excel
          </Button>
        </Box>

        {memberData && (
          <>
            <DisputeCards disputeStats={memberData.disputeStats} labelTotal="Refutaciones recibidas" />
            <Grid container spacing={3}>

              {/* Period summary table */}
              <Grid item xs={12} md={5}>
                <Card sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary" mb={2}>
                    Resumen del período
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    <Chip label={`${(memberData.summary?.totalHours || 0).toFixed(1)}h totales`}
                      color="primary" size="small" />
                    {memberData.summary?.totalOvertimeHours > 0 && (
                      <Chip label={`+${memberData.summary.totalOvertimeHours.toFixed(1)}h extra`}
                        color="warning" size="small" />
                    )}
                    <Chip label={`${memberData.summary?.projectsCount || 0} proyectos`}
                      variant="outlined" size="small" />
                  </Box>
                  {(memberData.charts?.byProjectTable || []).length > 0 ? (
                    <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Proyecto</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Horas</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Extra</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Act.</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {memberData.charts.byProjectTable.map(row => (
                            <TableRow key={row.projectId}>
                              <TableCell>{row.projectName}</TableCell>
                              <TableCell align="right">{(row.totalHours || 0).toFixed(1)}h</TableCell>
                              <TableCell align="right">
                                {row.overtimeHours > 0
                                  ? <Typography variant="body2" color="warning.dark" fontWeight={600}>+{row.overtimeHours.toFixed(1)}h</Typography>
                                  : '—'}
                              </TableCell>
                              <TableCell align="right">{row.activitiesCount}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Sin actividades en el período
                    </Typography>
                  )}
                </Card>
              </Grid>

              {/* Weekly / Daily hours chart — adapts to period mode */}
              <Grid item xs={12} md={7}>
                <Card sx={{ p: 2 }}>
                  {periodMode === 'week'
                    ? (() => {
                        const daily = memberData.charts?.dailyHoursByProject || [];
                        return (
                          <HighchartsReact highcharts={Highcharts} options={dailyByProjectOpts(
                            daily,
                            'Horas por día y proyecto · semana seleccionada'
                          )} />
                        );
                      })()
                    : (() => {
                        const weekly = memberData.charts?.weeklyHoursByProject || [];
                        return (
                          <HighchartsReact highcharts={Highcharts} options={weeklyByProjectStackedOpts(
                            weekly,
                            'Horas por semana y proyecto · mes seleccionado'
                          )} />
                        );
                      })()
                  }
                </Card>
              </Grid>
              {(memberData.charts?.byMilestone?.length > 0) && (
                <Grid item xs={12} md={5}>
                  <Card sx={{ p: 2 }}>
                    <HighchartsReact highcharts={Highcharts}
                      options={pieOpts(memberData.charts.byMilestone, 'Horas por Hito')} />
                  </Card>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {!memberData && !loadingMember && (
          <Box py={6} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Selecciona un miembro y presiona Consultar.
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* ── Tab 2: Disponibilidad */}
      <TabPanel value={tab} index={2}>
        {loadingAvail && <Box textAlign="center" py={4}><CircularProgress /></Box>}
        {availData && !loadingAvail && (
          <Card sx={{ p: 2 }}>
            <HighchartsReact highcharts={Highcharts} options={{
              chart: {
                type: 'bar',
                height: Math.max(300, (availData.availability || []).length * 52 + 100),
                backgroundColor: 'transparent'
              },
              title: {
                text: `Disponibilidad · ${periodLabel}`,
                style: { fontSize: '13px' }
              },
              xAxis: {
                categories: (availData.availability || []).map(d => d.name || d.email),
                gridLineWidth: 0
              },
              yAxis: {
                title: { text: 'Horas' },
                gridLineDashStyle: 'dot',
                max: Math.max(...(availData.availability || []).map(m => m.capacity || availData.capacity || 40), 1) * 1.15
              },
              tooltip: { valueSuffix: 'h', shared: false },
              credits: { enabled: false },
              legend: { enabled: true },
              plotOptions: { bar: { borderRadius: 2 } },
              series: [
                {
                  name: 'Horas registradas',
                  data: (availData.availability || []).map(d => parseFloat((d.hoursThisWeek || 0).toFixed(1))),
                  color: '#4FC3F7'
                },
                {
                  name: 'Capacidad configurada',
                  data: (availData.availability || []).map(m => m.capacity || availData.capacity || 40),
                  type: 'line',
                  color: '#888',
                  dashStyle: 'Dash',
                  marker: { enabled: false },
                  lineWidth: 1.5
                }
              ]
            }} />
          </Card>
        )}
        {!availData && !loadingAvail && (
          <Box py={6} textAlign="center">
            <Typography variant="body2" color="text.secondary">Sin datos para mostrar.</Typography>
          </Box>
        )}
      </TabPanel>
    </Box>
  );
}

